import { ISectionLogger } from "./ISectionLogger";
import { ISectionLoggerFactory } from "./ISectionLoggerFactory";
import { LoggingMessageOrGenerator, LoggingSectionType } from "./types";

/**
 * Logging module chceme využívat např. i z react modulů.
 * Tyto moduly vznikají importem - a tj. ještě před tím, než jsou k dispozici potřebné dependencies.
 * Tj. nejsme schopni si - při vzniku react modulu - rovnou získat plně inicializovaný section logger.
 *
 * Proto je pro react moduly (a další místa s podobným problémem) určen {@link LazyLoadingSectionLogger}.
 * - Ten je možné vytvořit ještě před získáním dependencies.
 * - Předpoklad: Logovat se přes něj bude až už jsou dependencies k dispozici.
 * - Při prvním zalogování si dependencies dotáhne a bude tak moci řádně logovat.
 *
 * Záměr je, aby logování bylo i v reactu (a dalších neobjektových místech) jednoduché a čitelné - příklad:
 * ```
 * // Např. v menu/logger.ts  (Dependencies ještě nejsou k dispozici)
 * const logger = new LazyLoadingSectionLogger(LoggingSection.mainMenu);
 * ...
 * // Např. v menu/saga.ts
 * someFunction() {
 *     ...
 *     logger.log("hello"); // Dependencies už jsou k dispozici
 *     ...
 * }
 * ```
 */
export class LazyLoadingSectionLogger implements ISectionLogger {
    /**
     * Implementace {@link ISectionLoggerFactory}, kterou chceme používat pro získání section loggerů.
     * Nebo `null`, pokud tato implementace ještě není k dispozici.
     *
     * Správné závislosti donastaví funkce {@link initDependencies}.
     */
    private static _sectionLoggerFactoryOrNull: ISectionLoggerFactory | undefined;

    /**
     * Oblast, které se tento logger týká.
     */
    private readonly _section: LoggingSectionType;

    /**
     * Sem se - při prvním zalogování - propíše "ten správný" section logger.
     * Předpoklad: Při prvním zalogování již {@link _sectionLoggerFactoryOrNull} obsahuje správně nainicializované závislosti.
     */
    private _suppliedSectionLogger: ISectionLogger | undefined;

    /**
     * Inicializuje závislosti, bez kterých tento logger nemůže logovat.
     * Určeno k externímu provolání (např. z `MainForm._registerDefaultTypes`).
     */
    public static initDependencies(sectionLoggerFactory: ISectionLoggerFactory): void {
        LazyLoadingSectionLogger._sectionLoggerFactoryOrNull = sectionLoggerFactory;
    }

    /**
     * Vytváří novou instanci lazy-loading-loggeru. Určeno pro externí volání - např. z react modulů.
     * Je tu kvůli zapouzdření. Aby bylo v budoucnu snazší implementaci nahradit za něco jiného.
     * @param section Oblast, které pro kterou bude vrácený logger logovat.
     */
    public static create(section: LoggingSectionType): LazyLoadingSectionLogger {
        return new LazyLoadingSectionLogger(section);
    }

    constructor(section: LoggingSectionType) {
        this._section = section;
    }

    public error = this._createFirstLog("error");
    public warn = this._createFirstLog("warn");
    public info = this._createFirstLog("info");
    public log = this._createFirstLog("log");
    public debug = this._createFirstLog("debug");
    public fineLog = this._createFirstLog("fineLog");
    public logException = this._createFirstLog("logException");
    public fineLogException = this._createFirstLog("fineLogException");

    public group(messageOrGenerator: LoggingMessageOrGenerator, ...args: any[]): void {
        this._firstLog("group", messageOrGenerator, ...args);
    }

    public groupCollapsed(messageOrGenerator: LoggingMessageOrGenerator, ...args: any[]): void {
        this._firstLog("groupCollapsed", messageOrGenerator, ...args);
    }

    public groupEnd(): void {
        this._firstLog("groupEnd");
    }

    /**
     * Vrací logovací funkci, kterou this-section-logger provede **první** zalogování.
     * Je to jednoduchá obálka nad {@link _firstLog}. Více viz {@link _firstLog}.
     */
    private _createFirstLog(logFunctionName: string): (...args: any[]) => void {
        return ((...args: any[]) => this._firstLog(logFunctionName, ...args));
    }

    /**
     * Logovací funkce určená pro **první** zalogování.
     * - Slouží jako prvotní implementace pro všechny metody z {@link ISectionLogger}
     * - Předpoklad: První zalogování proběhne až v době, kdy je již řádně nastavena {@link _sectionLoggerFactoryOrNull}. Viz {@link initDependencies}.
     * - Druhé a další zalogování již bude probíhat napřímo přes metody {@link _suppliedSectionLogger}.
     *
     * Dotáhne section logger do {@link _suppliedSectionLogger} a přes ten zaloguje.
     *
     * Zároveň nahradí implementaci všech metod z {@link ISectionLogger}, aby ukazovaly napřímo na metody z {@link _suppliedSectionLogger}.
     * (Kvůli rychlostní optimalizaci. Aby další logování již šlo napřímo.)
     *
     * @param logFunctionName Jméno funkce z {@link ISectionLogger}, které se to týká.
     * @param args Volitelné argumenty k zalogování.
     */
    private _firstLog(logFunctionName: string, ...args: any[]): void {
        if (this._suppliedSectionLogger) {
            throw new Error("First log: Section logger already supplied.");
        }
        this._suppliedSectionLogger = this._loadSectionLogger();
        this._replaceLoggingFunctions();
        this._getSuppliedLogFunction(logFunctionName)(...args);
    }

    private _loadSectionLogger(): ISectionLogger {
        if (!LazyLoadingSectionLogger._sectionLoggerFactoryOrNull) {
            throw new Error(`SectionLoggerFactory is not initialized yet (section=${this._section}).`);
        }
        return LazyLoadingSectionLogger._sectionLoggerFactoryOrNull.create(this._section);
    }

    /**
     * Nahradí implementace funkcí {@link ISectionLogger} za přímý odkaz na funkce z {@link _suppliedSectionLogger}.
     */
    private _replaceLoggingFunctions(): void {
        this._replaceLoggingFunction("error");
        this._replaceLoggingFunction("warn");
        this._replaceLoggingFunction("info");
        this._replaceLoggingFunction("log");
        this._replaceLoggingFunction("debug");
        this._replaceLoggingFunction("fineLog");
        this._replaceLoggingFunction("logException");
        this._replaceLoggingFunction("fineLogException");
        this._replaceLoggingFunction("group");
        this._replaceLoggingFunction("groupCollapsed");
        this._replaceLoggingFunction("groupEnd");
    }

    private _replaceLoggingFunction(logFunctionName: string): void {
        (this as any)[logFunctionName] = this._getSuppliedLogFunction(logFunctionName);
    }

    /**
     * Vrací zadanou funkci z {@link _suppliedSectionLogger} napojenou na {@link _suppliedSectionLogger},
     * takže se dá volat napřímo (bez explicitního zadávání kontextu).
     * @example
     * const logError = this._getSuppliedLogFunction("error");
     * logError("this is really serious ...");
     * @param logFunctionName Jméno funkce, která bude vrácena. Musí jít o jednu z funkcí z {@link ISectionLogger}.
     */
    private _getSuppliedLogFunction(logFunctionName: string): any {
        if (!this._suppliedSectionLogger) {
            throw new Error("Section logger not supplied yet.");
        }
        const logFunction = (this._suppliedSectionLogger as any)[logFunctionName];
        if (!logFunction) {
            throw new Error(`Supplied section logger doesn't contain function ${logFunctionName}.`);
        }
        return logFunction.bind(this._suppliedSectionLogger);
    }
}
